import React, { useEffect } from 'react';
import { SmileySad, ArrowClockwise } from '@phosphor-icons/react';
import ROUTES from '../../shared/constants/routes';
import { useNavigate, useRouteError } from 'react-router-dom';
import useEvents from '../../hooks/useEvents';
import { ANALYTICS_EVENTS } from '../../shared/constants/events';
import { buildErrorEventPayload, isStaleChunkError } from './errorPayload';

const CHUNK_RELOAD_FLAG = 'chunk_error_reloaded';

const ErrorPage: React.FC = () => {
	const navigate = useNavigate();
	const routeError = useRouteError();
	const { trackEvent } = useEvents();

	// Sin esto la pantalla es ciega: el único rastro de un error es que el
	// usuario mande una captura. La URL es el dato que identifica la causa.
	useEffect(() => {
		// Esta es la última pantalla que le queda al usuario: nada de aquí dentro
		// puede tumbarla, o se va a blanco dentro del propio error boundary.
		try {
			trackEvent({
				eventName: ANALYTICS_EVENTS.APP_ERROR_VIEWED,
				eventPayload: buildErrorEventPayload(
					routeError,
					window.location.href,
				),
			});
		} catch {
			// El reporte del error nunca vale romper la pantalla del error.
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Tras un deploy, los chunks viejos (hash) dejan de existir y la navegación
	// lazy falla con un fetch error. Una recarga obtiene el index.html nuevo y
	// lo resuelve; el flag evita un loop si el error persiste.
	if (
		isStaleChunkError(routeError) &&
		sessionStorage.getItem(CHUNK_RELOAD_FLAG) !== 'true'
	) {
		sessionStorage.setItem(CHUNK_RELOAD_FLAG, 'true');
		window.location.reload();
		return null;
	}
	const handleGoHome = () => {
		navigate(ROUTES.ROOT);
		window.location.reload();
	};

	return (
		<div className='min-h-screen flex flex-col items-center justify-center p-4 bg-gradient-to-br from-h-primary-90 to-neutral-50'>
			<div className='max-w-md w-full text-center -mt-10'>
				<div className='mb-8'>
					<div className='w-24 h-24 mx-auto mb-6 bg-h-primary-80 rounded-full flex items-center justify-center'>
						<SmileySad
							size={48}
							className='text-h-primary'
							weight='duotone'
						/>
					</div>

					<h1 className='text-6xl font-bold text-h-primary mb-2'>404</h1>

					<h2 className='text-2xl font-semibold text-h-dark mb-4'>
						Error desconocido
					</h2>

					<p className='text-neutral-600 leading-relaxed mb-8'>
						Lo sentimos, ocurrió un error al cargar la página, estamos
						trabajando para solucionarlo, intenta de nuevo más tarde.
					</p>
				</div>

				<div className='flex flex-col sm:flex-row gap-3 justify-center'>
					<button
						onClick={handleGoHome}
						className='flex items-center justify-center gap-2 px-6 py-3 bg-h-primary text-white rounded-xl font-medium'
					>
						<ArrowClockwise
							size={20}
							weight='bold'
							className='text-white'
						/>
						Recargar página
					</button>
				</div>
			</div>
		</div>
	);
};

export default ErrorPage;
